import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, limit, getDocs } from 'firebase/firestore';
import { db } from '../../core/firebase/firebase';
import { cache as prefetchCache } from '../../core/prefetch';
import store from '../../core/productStore';
import { trackCategory, getTopCategories, scoreProduct, trackSearch, getSearchHistory } from '../../core/taste';
import { normalizeCategory, catQueryValues } from '../../core/categoryUtils';
import ProductCard from '../../components/ProductCard';
import ThemeToggle from '../../components/ThemeToggle';
import SmartFilters from '../../components/SmartFilters';
import RecentlyViewed from '../../components/RecentlyViewed';
import TrendingProducts from '../../components/TrendingProducts';
import ProductRecommendations from '../../components/ProductRecommendations';
import categories from '../category/categories';
import { IconSearch, IconX, IconCompass } from '../../components/Icons';

const SLUG_TO_CAT = {
  'vetements':    'clothing',
  'chaussures':   'shoes',
  'electronique': 'electronics',
  'jeux-video':   'gaming',
  'laptops':      'laptops',
  'cameras':      'cameras',
  'maison':       'home',
};
const CAT_TO_SLUG = Object.fromEntries(Object.entries(SLUG_TO_CAT).map(([s, c]) => [c, s]));

const SORT_OPTIONS = [
  { value: 'smart',      label: '✦ Pour toi'  },
  { value: 'likes',      label: 'Populaires'  },
  { value: 'price_asc',  label: 'Prix ↑'      },
  { value: 'price_desc', label: 'Prix ↓'      },
];

async function fetchCat(catValue) {
  const key = catValue || 'all';
  if (prefetchCache.has(key)) return prefetchCache.get(key);
  const [ebaySnap, userSnap] = await Promise.all([
    getDocs(query(collection(db, 'ebay_products'), where('category', '==', catValue), limit(40))),
    getDocs(query(collection(db, 'user_products'), where('category', 'in', catQueryValues(catValue)), limit(40))),
  ]);
  const ebay  = ebaySnap.docs.map(d => ({ id: d.id, ...d.data(), _col: 'ebay_products', isCreator: false })).filter(p => p.available !== false);
  const users = userSnap.docs.map(d => ({ id: d.id, ...d.data(), _col: 'user_products',  isCreator: true  }));
  const result = [...users, ...ebay];
  prefetchCache.set(key, result);
  return result;
}

function dedupe(list) {
  const seen = new Set();
  return list.filter(p => {
    const k = (p._col || '') + p.id;
    return seen.has(k) ? false : (seen.add(k), true);
  });
}

function applySort(list, sort, topCats) {
  const c = [...list];
  if (sort === 'smart')      c.sort((a, b) => scoreProduct(b, topCats) - scoreProduct(a, topCats));
  if (sort === 'likes')      c.sort((a, b) => (b.likes || 0) - (a.likes || 0));
  if (sort === 'price_asc')  c.sort((a, b) => (parseFloat(a.price)||0) - (parseFloat(b.price)||0));
  if (sort === 'price_desc') c.sort((a, b) => (parseFloat(b.price)||0) - (parseFloat(a.price)||0));
  return c;
}

export default function ExplorePage() {
  const [products,    setProducts]    = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [search,      setSearch]      = useState('');
  const [category,    setCategory]    = useState('all');
  const [sort,        setSort]        = useState('smart');
  const [showFilters, setShowFilters] = useState(false);
  const [searchHistory, setSearchHistory] = useState(() => getSearchHistory());

  const topCats   = useRef(getTopCategories());
  const unsubs    = useRef([]);
  const searchRef = useRef(null);

  const isIdle = category === 'all' && !search.trim();

  // ── Charge les produits ──
  useEffect(() => {
    unsubs.current.forEach(u => u());
    unsubs.current = [];

    topCats.current = getTopCategories();
    const tops = topCats.current.slice(0, 3);

    // ── MODE IDLE : feed personnalisé ──
    if (isIdle) {
      // 1. Produits depuis le store local (instantané)
      const storeAll = store.get();

      if (tops.length > 0) {
        // A. Produits des catégories préférées en premier
        const fromStore = storeAll.filter(p => tops.includes(normalizeCategory(p.category)));
        if (fromStore.length >= 6) {
          setProducts(applySort(fromStore, sort, tops));
          setLoading(false);
        } else {
          setLoading(true);
        }

        // B. Fetch Firebase pour les top catégories
        Promise.all(tops.map(cat => fetchCat(cat)))
          .then(results => {
            const all = dedupe(results.flat());
            store.add(all);
            topCats.current = getTopCategories();
            setProducts(applySort(all, sort, topCats.current));
          })
          .catch(console.error)
          .finally(() => setLoading(false));

      } else {
        // Aucune préférence encore → produits tendances du store
        const trending = applySort(storeAll, 'likes', []);
        if (trending.length > 0) {
          setProducts(trending.slice(0, 24));
          setLoading(false);
        } else {
          setLoading(false);
          setProducts([]);
        }
      }
      return;
    }

    // ── MODE CATÉGORIE / RECHERCHE ──
    const catValue = SLUG_TO_CAT[category] ?? null;

    const fromStore = store.get().filter(p => !catValue || normalizeCategory(p.category) === catValue);
    if (fromStore.length > 0) {
      setProducts(applySort(fromStore, sort, topCats.current));
      setLoading(false);
      if (catValue) fetchCat(catValue).then(fresh => {
        store.add(fresh);
        setProducts(applySort(fresh, sort, topCats.current));
      }).catch(() => {});
      return;
    }

    const cacheKey = catValue || 'all';
    if (prefetchCache.has(cacheKey)) {
      setProducts(applySort(prefetchCache.get(cacheKey), sort, topCats.current));
      setLoading(false);
      return;
    }

    setLoading(true);
    if (catValue) {
      fetchCat(catValue).then(data => {
        store.add(data);
        topCats.current = getTopCategories();
        setProducts(applySort(data, sort, topCats.current));
      }).catch(console.error).finally(() => setLoading(false));
    }
  }, [category]); // eslint-disable-line

  // re-trier sans re-fetch
  useEffect(() => {
    topCats.current = getTopCategories();
    setProducts(prev => applySort([...prev], sort, topCats.current));
  }, [sort]);

  // track la recherche (debounce 700ms)
  useEffect(() => {
    if (!search.trim() || search.trim().length < 2) return;
    const t = setTimeout(() => {
      trackSearch(search.trim());
      setSearchHistory(getSearchHistory());
    }, 700);
    return () => clearTimeout(t);
  }, [search]);

  const handleCategoryClick = (slug) => {
    const val = SLUG_TO_CAT[slug];
    if (val) trackCategory(val);
    setSearch('');
    setCategory(p => p === slug ? 'all' : slug);
  };

  // filtre texte côté client
  const t = search.trim().toLowerCase();
  const displayed = t
    ? products.filter(p =>
        (p.title || '').toLowerCase().includes(t) ||
        (p.brand || '').toLowerCase().includes(t) ||
        (p.description || '').toLowerCase().includes(t))
    : products;

  // sections "Pour toi" / "Découvrir" en mode smart
  const topCatsList  = topCats.current;
  const forYou       = sort === 'smart' && !t && topCatsList.length > 0 && !isIdle
    ? displayed.filter(p => topCatsList.includes(normalizeCategory(p.category))).slice(0, 6)
    : [];
  const forYouIds    = new Set(forYou.map(p => p._col + p.id));
  const restProducts = forYou.length > 0 ? displayed.filter(p => !forYouIds.has(p._col + p.id)) : displayed;

  return (
    <div style={{ background: 'var(--bg-secondary)', minHeight: '100vh', paddingBottom: 90, transition: 'background-color 0.3s ease' }}>
      {/* Header Premium */}
      <div style={{ background: 'var(--bg-primary)', borderBottom: '1px solid var(--border-color)', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 50 }}>
        <h1 style={{ margin: 0, fontSize: '18px', fontWeight: '700', background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
          Découvrez ✨
        </h1>
        <ThemeToggle />
      </div>

      {/* ── Barre de recherche Premium ── */}
      <div style={{ padding: '16px', background: 'var(--bg-primary)', borderBottom: '1px solid var(--border-color)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              background: 'var(--bg-secondary)',
              borderRadius: '12px',
              padding: '10px 14px',
              border: '1.5px solid var(--border-color)',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#6366f1';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-color)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <span style={{ fontSize: '16px' }}>🔍</span>
            <input
              ref={searchRef}
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                background: 'none',
                fontSize: '14px',
                color: 'var(--text-primary)',
              }}
              placeholder="Rechercher des produits, marques..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoComplete="off"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '18px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                ✕
              </button>
            )}
          </div>
          <button
            onClick={() => setShowFilters(v => !v)}
            style={{
              padding: '10px 16px',
              background: showFilters ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' : 'var(--bg-secondary)',
              color: showFilters ? 'white' : 'var(--text-primary)',
              border: `1.5px solid ${showFilters ? 'transparent' : 'var(--border-color)'}`,
              borderRadius: '12px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '13px',
              transition: 'all 0.2s ease',
            }}
          >
            ⚙️ Filtres
          </button>
        </div>
      </div>

      {/* ── Historique recherches (premium) ── */}
      {isIdle && !search && searchHistory.length > 0 && (
        <div style={{ padding: '12px 16px', background: 'var(--bg-primary)', display: 'flex', gap: '8px', overflowX: 'auto', scrollBehavior: 'smooth' }}>
          <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-tertiary)', textTransform: 'uppercase', whiteSpace: 'nowrap', marginRight: '4px' }}>Récent:</span>
          {searchHistory.slice(0, 6).map(term => (
            <button
              key={term}
              onClick={() => setSearch(term)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
                border: '1px solid rgba(99, 102, 241, 0.2)',
                borderRadius: '20px',
                padding: '6px 12px',
                fontSize: '12px',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                fontWeight: '500',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)';
                e.target.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)';
                e.target.style.color = 'var(--text-secondary)';
              }}
            >
              🕐 {term}
            </button>
          ))}
        </div>
      )}

      {/* ── Catégories (chips) ── */}
      <div style={{ padding: '12px 16px', display: 'flex', gap: '8px', overflowX: 'auto', scrollBehavior: 'smooth', background: 'var(--bg-primary)', borderBottom: '1px solid var(--border-color)' }}>
        <button
          onClick={() => { setSearch(''); setCategory('all'); }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 14px',
            borderRadius: '20px',
            border: `1.5px solid ${category === 'all' ? 'transparent' : 'var(--border-color)'}`,
            background: category === 'all' ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' : 'var(--bg-secondary)',
            color: category === 'all' ? 'white' : 'var(--text-primary)',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '12px',
            whiteSpace: 'nowrap',
            transition: 'all 0.2s ease',
          }}
        >
          🧭 Tous
        </button>
        {categories.map(cat => (
          <button
            key={cat.slug}
            onClick={() => handleCategoryClick(cat.slug)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              borderRadius: '20px',
              border: `1.5px solid ${category === cat.slug ? 'transparent' : 'var(--border-color)'}`,
              background: category === cat.slug ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' : 'var(--bg-secondary)',
              color: category === cat.slug ? 'white' : 'var(--text-primary)',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '12px',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              if (category !== cat.slug) {
                e.target.style.borderColor = '#6366f1';
                e.target.style.boxShadow = '0 0 0 2px rgba(99, 102, 241, 0.1)';
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = 'var(--border-color)';
              e.target.style.boxShadow = 'none';
            }}
          >
            <span>{cat.emoji}</span> {cat.label}
          </button>
        ))}
      </div>

      {/* ── Filtre tri (si activé) ── */}
      {showFilters && (
        <SmartFilters onClose={() => setShowFilters(false)} onFilterChange={(filters) => { /* TODO */ }} />
      )}

      {/* ── Contenu Principal ── */}
      <div style={{ padding: '0 16px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Produits récemment vus */}
        <div style={{ paddingTop: '24px' }}>
          <RecentlyViewed />
        </div>

        {/* Produits tendance */}
        {products.length > 0 && (
          <div style={{ paddingBottom: '24px' }}>
            <TrendingProducts products={products.slice(0, 12)} />
          </div>
        )}

        {/* Recommandations personnalisées */}
        {products.length > 0 && (
          <ProductRecommendations products={products.slice(0, 8)} reason="basé sur vos favoris" />
        )}

        {/* État de chargement */}
        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '16px', marginBottom: '32px' }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} style={{ borderRadius: '12px', background: 'linear-gradient(90deg, var(--bg-tertiary) 0%, var(--bg-secondary) 50%, var(--bg-tertiary) 100%)', backgroundSize: '1000px 100%', animation: 'shimmer 2s infinite', aspectRatio: '1' }} />
            ))}
          </div>
        ) : isIdle && products.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>🛍️</div>
            <h2 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)' }}>Commencez à explorer</h2>
            <p style={{ margin: 0, color: 'var(--text-secondary)', marginBottom: '24px' }}>Aimez des produits pour des recommandations personnalisées</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px' }}>
              {categories.map(cat => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryClick(cat.slug)}
                  style={{
                    padding: '16px',
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
                    border: '1px solid rgba(99, 102, 241, 0.2)',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontSize: '28px',
                    marginBottom: '4px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)';
                    e.currentTarget.style.color = 'white';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)';
                    e.currentTarget.style.color = 'inherit';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {cat.emoji}
                  <div style={{ fontSize: '12px', marginTop: '4px', fontWeight: '600' }}>{cat.label}</div>
                </button>
              ))}
            </div>
          </div>
        ) : displayed.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
            <h3 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: '700', color: 'var(--text-primary)' }}>Aucun résultat trouvé</h3>
            <p style={{ margin: 0, color: 'var(--text-secondary)', marginBottom: '16px' }}>Essayez une autre recherche</p>
            <button
              onClick={() => { setSearch(''); setCategory('all'); }}
              style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '13px',
              }}
            >
              Réinitialiser les filtres
            </button>
          </div>
        ) : (
          <>
            {/* Section "Pour toi" */}
            {forYou.length > 0 && (
              <div style={{ marginBottom: '32px' }}>
                <div style={{ marginBottom: '16px' }}>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-primary)' }}>
                    <span>✦</span> Pour vous
                  </h3>
                  <p style={{ margin: '4px 0 0 28px', fontSize: '12px', color: 'var(--text-secondary)' }}>Basé sur vos préférences</p>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '16px' }}>
                  {forYou.map(p => <ProductCard key={`${p._col}${p.id}`} product={p} />)}
                </div>
              </div>
            )}

            {/* Grille principale */}
            {(isIdle || forYou.length === 0) && (
              <div>
                {isIdle && topCatsList.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-primary)' }}>
                      <span>🔥</span> Populaire
                    </h3>
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '16px', marginBottom: '32px' }}>
                  {displayed.map(p => <ProductCard key={`${p._col}${p.id}`} product={p} />)}
                </div>
              </div>
            )}

            {/* Section "Découvrir" */}
            {!isIdle && forYou.length > 0 && restProducts.length > 0 && (
              <div>
                <div style={{ marginBottom: '16px' }}>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-primary)' }}>
                    <span>🎯</span> À découvrir
                  </h3>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '16px', marginBottom: '32px' }}>
                  {restProducts.map(p => <ProductCard key={`${p._col}${p.id}`} product={p} />)}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────

const skels = n => Array.from({ length: n }).map((_, i) => <div key={i} style={s.skel} />);

function IdleEmpty({ topCats, onPick }) {
  const topSlug   = topCats.map(c => CAT_TO_SLUG[c]).filter(Boolean);
  const favCats   = categories.filter(c => topSlug.includes(c.slug));
  const otherCats = categories.filter(c => !topSlug.includes(c.slug));
  const ordered   = [...favCats, ...otherCats];

  return (
    <div style={s.idle}>
      <div style={s.heroBox}>
        <p style={s.heroTitle}>Explorez Nexsap</p>
        <p style={s.heroSub}>Aimez des produits pour recevoir des recommandations personnalisées</p>
      </div>
      <p style={s.secTitle}>Catégories</p>
      <div style={s.catGrid}>
        {ordered.map(cat => (
          <button key={cat.slug}
            style={{ ...s.catCard, ...(topSlug.includes(cat.slug) ? s.catCardFav : {}) }}
            onClick={() => onPick(cat.slug)}>
            <div style={{ ...s.catIcon, ...(topSlug.includes(cat.slug) ? s.catIconFav : {}) }}>
              <span style={{ fontSize: 24 }}>{cat.emoji}</span>
            </div>
            <span style={s.catLbl}>{cat.label}</span>
            {topSlug.includes(cat.slug) && <span style={s.favDot}>✦</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

function Chip({ icon, label, active, onClick }) {
  return (
    <button style={{ ...s.chip, ...(active ? s.chipOn : {}) }} onClick={onClick}>
      {icon}
      <span style={{ fontSize: 12, fontWeight: active ? 600 : 400, color: active ? '#fff' : '#555' }}>{label}</span>
    </button>
  );
}
function Pill({ label, active, onClick }) {
  return <button style={{ ...s.pill, ...(active ? s.pillOn : {}) }} onClick={onClick}>{label}</button>;
}
function Empty({ onReset }) {
  return (
    <div style={s.empty}>
      <span style={{ fontSize: 44 }}>🔍</span>
      <p style={{ fontSize: 16, fontWeight: 700, color: '#444', margin: 0 }}>Aucun résultat</p>
      <button style={s.resetLink} onClick={onReset}>Réinitialiser</button>
    </div>
  );
}
function FilterSVG({ color }) {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="6" x2="20" y2="6" /><line x1="8" y1="12" x2="16" y2="12" /><line x1="11" y1="18" x2="13" y2="18" />
    </svg>
  );
}

// ─── Styles ──────────────────────────────────────────────────
const s = {
  page         : { background: '#f7f8fa', minHeight: '100vh', paddingBottom: 90 },
  searchRow    : { display: 'flex', alignItems: 'center', gap: 10, padding: '16px 16px 0' },
  searchWrap   : { flex: 1, display: 'flex', alignItems: 'center', gap: 10, background: '#fff', borderRadius: 14, padding: '0 14px', height: 48, border: '1.5px solid #eaeaea', boxShadow: '0 1px 6px rgba(0,0,0,0.05)' },
  input        : { flex: 1, border: 'none', outline: 'none', background: 'none', fontSize: 15, color: '#1a1a1a' },
  iconBtn      : { background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 4 },
  filterBtn    : { width: 48, height: 48, borderRadius: 14, border: '1.5px solid #eaeaea', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, boxShadow: '0 1px 6px rgba(0,0,0,0.05)' },
  filterBtnOn  : { background: '#1a1a2e', border: '1.5px solid #1a1a2e' },

  historyRow   : { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, padding: '10px 16px 0' },
  historyLabel : { fontSize: 11, fontWeight: 700, color: '#bbb', textTransform: 'uppercase', letterSpacing: 0.5, marginRight: 2 },
  historyChip  : { display: 'flex', alignItems: 'center', gap: 4, background: '#fff', border: '1px solid #eee', borderRadius: 99, padding: '5px 12px', fontSize: 12, color: '#555', cursor: 'pointer', fontWeight: 500 },
  historyIcon  : { fontSize: 11 },

  chips        : { display: 'flex', overflowX: 'auto', gap: 8, padding: '12px 16px 8px', scrollbarWidth: 'none' },
  chip         : { display: 'flex', alignItems: 'center', gap: 5, background: '#fff', border: '1.5px solid #eaeaea', borderRadius: 99, padding: '6px 12px', cursor: 'pointer', flexShrink: 0 },
  chipOn       : { background: '#1a1a2e', border: '1.5px solid #1a1a2e' },
  panel        : { margin: '4px 16px', background: '#fff', borderRadius: 16, padding: 16, border: '1.5px solid #eaeaea', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' },
  sectionLbl   : { fontSize: 12, fontWeight: 700, color: '#888', marginBottom: 10, letterSpacing: 0.5, textTransform: 'uppercase' },
  pillRow      : { display: 'flex', flexWrap: 'wrap', gap: 8 },
  pill         : { padding: '6px 14px', borderRadius: 99, border: '1.5px solid #eaeaea', background: '#f7f8fa', fontSize: 13, fontWeight: 500, cursor: 'pointer', color: '#555' },
  pillOn       : { background: '#1a1a2e', color: '#fff', border: '1.5px solid #1a1a2e' },
  sectionHeader: { display: 'flex', alignItems: 'baseline', gap: 8, padding: '14px 16px 8px' },
  sectionTitle : { fontSize: 16, fontWeight: 700, color: '#1a1a1a' },
  sectionSub   : { fontSize: 12, color: '#aaa' },
  grid         : { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, padding: '4px 12px 12px' },
  skel         : { width: '100%', aspectRatio: '1/1', borderRadius: 14, background: 'linear-gradient(110deg,#eee 30%,#f5f5f5 50%,#eee 70%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' },

  idle         : { padding: '0 16px' },
  heroBox      : { background: 'linear-gradient(135deg, #1a1a2e 0%, #4361ee 100%)', borderRadius: 18, padding: '20px', margin: '16px 0', textAlign: 'center' },
  heroTitle    : { color: '#fff', fontSize: 18, fontWeight: 700, margin: '0 0 6px' },
  heroSub      : { color: 'rgba(255,255,255,0.7)', fontSize: 13, margin: 0, lineHeight: 1.5 },
  secTitle     : { fontSize: 15, fontWeight: 700, color: '#333', marginBottom: 14, marginTop: 8 },
  catGrid      : { display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 24 },
  catCard      : { flex: '1 1 calc(33% - 10px)', minWidth: 90, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, background: '#fff', border: '1.5px solid #eaeaea', borderRadius: 16, padding: '16px 8px', cursor: 'pointer', position: 'relative' },
  catCardFav   : { border: '1.5px solid #4361ee', background: '#f0f4ff' },
  catIcon      : { width: 52, height: 52, borderRadius: 26, background: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  catIconFav   : { background: '#4361ee' },
  catLbl       : { fontSize: 11, fontWeight: 600, color: '#555', textAlign: 'center' },
  favDot       : { position: 'absolute', top: 6, right: 8, fontSize: 10, color: '#4361ee' },
  empty        : { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, paddingTop: 80 },
  resetLink    : { background: 'none', border: 'none', color: '#6666cc', fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: '8px 0' },
};
